import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../../services/api";
import { DASHBOARD_SIGNATURES_PAGE_SIZE } from "../constants/dashboardListPagination";
import { fetchPendingSignatures } from "../services/ownerPatrimonyService";
import {
  IOwnerKenloEndpoints,
  IPendingSignaturesData,
  OwnerDocumentStatusFilter,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { PendingSignaturesQuery } from "../utils/buildPendingSignaturesParams";

interface UsePendingSignaturesOptions {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
}

interface UsePendingSignaturesResult {
  data: IPendingSignaturesData | null;
  status: OwnerPatrimonyRequestStatus;
  statusFilter: OwnerDocumentStatusFilter;
  isForbidden: boolean;
  isNotConfigured: boolean;
  successMessage: string | null;
  setStatusFilter: (status: OwnerDocumentStatusFilter) => void;
  setPage: (page: number) => void;
  retry: () => Promise<void>;
  refreshList: () => Promise<void>;
  markSignedAndRefresh: () => Promise<void>;
  clearSuccessMessage: () => void;
}

function createDefaultQuery(): PendingSignaturesQuery {
  return {
    status: "pending",
    page: 1,
    perPage: DASHBOARD_SIGNATURES_PAGE_SIZE,
  };
}

export function usePendingSignatures({
  accessToken,
  enabled,
  endpoints,
}: UsePendingSignaturesOptions): UsePendingSignaturesResult {
  const [data, setData] = useState<IPendingSignaturesData | null>(null);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [query, setQuery] = useState<PendingSignaturesQuery>(createDefaultQuery);
  const [isForbidden, setIsForbidden] = useState(false);
  const [isNotConfigured, setIsNotConfigured] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const latestRequestIdRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const loadPendingSignatures = useCallback(
    async (
      requestQuery: PendingSignaturesQuery,
      options?: { preserveSuccessMessage?: boolean; silent?: boolean }
    ) => {
      if (!enabled || !accessToken) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      queryRef.current = requestQuery;
      setQuery(requestQuery);

      if (!options?.silent) {
        setStatus("loading");
      }

      setIsForbidden(false);
      setIsNotConfigured(false);

      if (!options?.preserveSuccessMessage) {
        setSuccessMessage(null);
      }

      try {
        api.defaults.headers.authorization = `Bearer ${accessToken}`;
        const response = await fetchPendingSignatures(requestQuery, endpoints);

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        const lastPage = Math.max(1, response.pagination?.last_page ?? 1);
        if (requestQuery.page > lastPage) {
          await loadPendingSignatures(
            { ...requestQuery, page: lastPage },
            { ...options, silent: true }
          );
          return;
        }

        setData(response);
        setStatus("success");
        hasLoadedOnceRef.current = true;
      } catch (error) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        if (axios.isAxiosError(error)) {
          if (error.response?.status === 403) {
            setIsForbidden(true);
            if (!hasLoadedOnceRef.current) {
              setData(null);
            }
            setStatus("error");
            return;
          }

          if (error.response?.status === 404) {
            setIsNotConfigured(true);
            if (!hasLoadedOnceRef.current) {
              setData(null);
            }
            setStatus("error");
            return;
          }
        }

        if (!hasLoadedOnceRef.current) {
          setData(null);
          setStatus("error");
          return;
        }

        setStatus("success");
      }
    },
    [accessToken, enabled, endpoints]
  );

  const refreshList = useCallback(async () => {
    await loadPendingSignatures(queryRef.current, {
      preserveSuccessMessage: true,
      silent: true,
    });
  }, [loadPendingSignatures]);

  const markSignedAndRefresh = useCallback(async () => {
    setSuccessMessage(
      "Assinatura registrada. Aguarde as demais assinaturas para concluir o contrato."
    );
    await loadPendingSignatures(queryRef.current, {
      preserveSuccessMessage: true,
      silent: true,
    });
  }, [loadPendingSignatures]);

  const setStatusFilter = useCallback(
    (statusFilter: OwnerDocumentStatusFilter) => {
      void loadPendingSignatures({
        ...queryRef.current,
        status: statusFilter,
        page: 1,
      });
    },
    [loadPendingSignatures]
  );

  const setPage = useCallback(
    (page: number) => {
      void loadPendingSignatures({
        ...queryRef.current,
        page,
      });
    },
    [loadPendingSignatures]
  );

  const retry = useCallback(async () => {
    await loadPendingSignatures(queryRef.current);
  }, [loadPendingSignatures]);

  useEffect(() => {
    if (!enabled || !accessToken) {
      setData(null);
      setStatus("idle");
      setQuery(createDefaultQuery());
      setIsForbidden(false);
      setIsNotConfigured(false);
      setSuccessMessage(null);
      hasLoadedOnceRef.current = false;
      return;
    }

    void loadPendingSignatures(createDefaultQuery());
  }, [accessToken, enabled, loadPendingSignatures]);

  return {
    data,
    status,
    statusFilter: query.status,
    isForbidden,
    isNotConfigured,
    successMessage,
    setStatusFilter,
    setPage,
    retry,
    refreshList,
    markSignedAndRefresh,
    clearSuccessMessage: () => setSuccessMessage(null),
  };
}
